import { composeThemeFromProps, Compose } from '@css-modules-theme/react';
import NP from 'number-precision';
import { forwardRef } from 'react';
import { Minus, Plus, ChevronUp, ChevronDown } from 'react-feather';
import defaultStyles from './styles/InputNumber.module.scss.js';
import { useInputNumber } from './utils/useInputNumber.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Input } from '../input/Input.js';
import { omit } from '../../utils/omit.js';
import { Button } from '../button/Button.js';

NP.enableBoundaryChecking(false);
const ID_SUFFIX = '_input_number';
const EMBED_BUTTON = 'embed-button';
const InputNumber = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    theme,
    style,
    defaultValue,
    value,
    disabled,
    validationStatus,
    readOnly,
    placeholder,
    name,
    hideControl,
    suffix,
    prefix,
    icons,
    size,
    precision,
    formatter,
    onBlur,
    onFocus,
    onChange,
    onKeyDown,
    max = 1000,
    min = 10,
    step = 1,
    mode = 'embed',
    parser = input => input.replace(/[^\w\.-]+/g, ''),
    idSuffix = ID_SUFFIX,
    label,
    ...rest
  } = props;
  const styles = composeThemeFromProps(defaultStyles, {
    theme
  }, {
    compose: Compose.Merge
  });
  const {
    getDisplayInputValue,
    inputEventHandlers,
    getControlButtonEventsHandlers,
    shouldRenderButton,
    shouldRenderLayer
  } = useInputNumber({
    precision,
    step,
    defaultValue,
    inputVal: value,
    onChange,
    min,
    max,
    disabled,
    formatter,
    parser,
    onKeyDown,
    onFocus,
    onBlur,
    readOnly,
    hideControl,
    mode
  });
  const isCustomMinusIcon = icons && icons.minus ? icons.minus : /*#__PURE__*/jsx(Minus, {});
  const isCustomPlusIcon = icons && icons.plus ? icons.plus : /*#__PURE__*/jsx(Plus, {});
  const renderStepButton = (method, icon) => {
    const value = getDisplayInputValue();
    return /*#__PURE__*/jsx(Button, {
      variant: mode === EMBED_BUTTON ? 'link' : 'solid',
      flat: true,
      status: "secondary",
      size: mode !== 'embed' ? size : 'xs',
      ariaLabel: method === 'plus' ? 'Increase' : 'Decrease',
      name: method === 'plus' ? 'Increase' : 'Decrease',
      disabled: method === 'plus' ? value >= max : value <= min,
      theme: {
        root: mode === 'embed' ? styles.button_embed : styles.button,
        disabled: styles.button_disabled,
        sm: mode !== 'embed' && styles.button_sm,
        xs: mode !== 'embed' && styles.button_xs,
        md: mode !== 'embed' && styles.button_md,
        lg: mode !== 'embed' && styles.button_lg
      },
      ...getControlButtonEventsHandlers(method),
      children: icon
    });
  };
  const renderStepButtons = () => {
    if (shouldRenderLayer && suffix) {
      return /*#__PURE__*/jsxs(Fragment, {
        children: [/*#__PURE__*/jsxs("div", {
          className: styles.step_layer,
          children: [renderStepButton('plus', icons && icons.up ? icons.up : /*#__PURE__*/jsx(ChevronUp, {})), renderStepButton('minus', icons && icons.down ? icons.down : /*#__PURE__*/jsx(ChevronDown, {}))]
        }), /*#__PURE__*/jsx("span", {
          className: styles.suffix,
          children: suffix
        })]
      });
    }
    if (shouldRenderLayer && !suffix) {
      return /*#__PURE__*/jsxs("div", {
        className: styles.step_layer,
        children: [renderStepButton('plus', icons && icons.up ? icons.up : /*#__PURE__*/jsx(ChevronUp, {})), renderStepButton('minus', icons && icons.down ? icons.down : /*#__PURE__*/jsx(ChevronDown, {}))]
      });
    }
    if (!shouldRenderLayer && suffix) {
      return /*#__PURE__*/jsx("span", {
        className: styles.suffix,
        children: suffix
      });
    }
    return null;
  };
  return /*#__PURE__*/jsx(Input, {
    ref: ref,
    ...omit(rest, ['allowClear']),
    ...inputEventHandlers,
    theme: {
      inner: mode === 'embed' && styles.root,
      has_addons: mode === 'button' && styles.root_button,
      input: styles.number_input,
      ...theme
    },
    type: "number",
    inputMode: "numeric",
    label: label,
    name: name,
    style: style,
    size: size,
    validationStatus: validationStatus,
    disabled: disabled,
    readOnly: readOnly,
    value: getDisplayInputValue(),
    placeholder: placeholder,
    prefix: mode === EMBED_BUTTON ? renderStepButton('minus', isCustomMinusIcon) : prefix,
    allowClear: false,
    idSuffix: idSuffix,
    suffix: mode === EMBED_BUTTON ? renderStepButton('plus', isCustomPlusIcon) : renderStepButtons(),
    addBefore: shouldRenderButton && renderStepButton('minus', isCustomMinusIcon),
    addAfter: shouldRenderButton && renderStepButton('plus', isCustomPlusIcon)
  });
});

export { InputNumber };
